import React from 'react'
import Select from 'react-select';
import PopUp from 'Components/PopUp'
import { dateTimeFormat, generateReportName } from 'GenericForms/Helper';
import { Row, Col, Label, Button } from 'reactstrap';
import { customStyle } from 'shared/CommonCSS';
import { queryKeyes } from 'shared/queryKeys';
import apiGlobal from 'global/api.global';
import { RootState } from 'index';
import { useSelector } from 'react-redux';
import env from 'environment_system/env_system';
import ErrorTooltip from 'Components/ErrorTooltip';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import { queryClient } from 'react-query/queryClient';

interface DateRangePopupProps {
    dateRangePopup: any;
    setDateRangePopup: (value: boolean) => void;
    customReportUseFormik: any; // You can refine this to FormikProps<any> if using Formik
    minDate: any;
    maxDate: any;
    setFilterValues: (values: any) => void;
    setisPopupOpen: (isPopupOpen: boolean) => void;
    isPopupOpen: boolean;
}

const DateRangePopup = ({
    dateRangePopup,
    setDateRangePopup,
    customReportUseFormik,
    minDate,
    maxDate,
    setFilterValues,
    setisPopupOpen,
    isPopupOpen,
}: DateRangePopupProps) => {
    const [startReports, setStartReports] = React.useState<any>([]);
    const [endReports, setEndReports] = React.useState<any>([]);
    const [startLoading, setStartLoading] = React.useState<boolean>(false);
    const [endLoading, setEndLoading] = React.useState<boolean>(false);
    const [startRefreshkey, setStartRefreshKey] = React.useState<number>(0);
    const [endRefreshkey, setEndRefreshKey] = React.useState<number>(0);

    const { VesselID, } = useSelector((state: RootState) => state.Reporting);

    const fetchStartReports = (startDate: string) => {
        if (!startDate) return;
        setStartLoading(true);
        apiGlobal
            .get(queryKeyes.vessel.GetReportsByStartDate.url(VesselID, startDate))
            .then((res: any) => {
                setStartLoading(false);
                setStartReports(res?.data);
            })
            .catch(() => {
                setStartLoading(false);
            });
        setStartRefreshKey(startRefreshkey + 1);
    };

    const fetchEndReports = (endDate: string) => {
        if (!endDate) return;
        setEndLoading(true);
        apiGlobal
            .get(queryKeyes.vessel.GetReportsByEndDate.url(VesselID, endDate))
            .then((res: any) => {
                setEndReports(res?.data);
                setEndLoading(false);
            })
            .catch(() => {
                setEndReports([]);
                setEndLoading(false);
            });
        setEndRefreshKey(startRefreshkey + 1);
    };

    const handleStartDateChange = (e: any) => {
        customReportUseFormik?.handleChange(e);
        // Clear the selected start report when date changes
        customReportUseFormik?.setFieldValue("startReport", null);

        const startDate = e.target.value;
        if (startDate) {
            fetchStartReports(startDate);
        } else {
            setStartReports([]);
        }
    };

    const handleEndDateChange = (e: any) => {
        customReportUseFormik?.handleChange(e);
        // Clear the selected end report when date changes
        customReportUseFormik?.setFieldValue("endReport", null);

        const endDate = e.target.value;
        if (endDate) {
            fetchEndReports(endDate);
        } else {
            setEndReports([]);
        }
    };

    /**
     * Handle popup close event
     */
    const handlePopupClose = () => {
        // Reset only date range and report selections, keep vessel_id
        const { vessel_id } = customReportUseFormik.values;
        customReportUseFormik.setValues({
            startDate: null,
            endDate: null,
            startReport: null,
            endReport: null,
            vessel_id: vessel_id,
            emails: [''],
        });
    }

    return (
        <PopUp
            state={dateRangePopup}
            setState={(value: boolean) => {
                if (value === false) {
                    handlePopupClose();
                }
                setDateRangePopup(value);
            }}
            body={
                <Row>
                    <Col lg={6} className="px-4">
                        <Label className="mb-0 asteric">Start date</Label>
                        <input
                            type="date"
                            name="startDate"
                            id="startDate_field"
                            min={minDate}
                            max={maxDate}
                            className="datetimepicker text-uppercase form-control mt-0 max-width-10 mb-2"
                            defaultValue={dateTimeFormat(
                                customReportUseFormik?.values.startDate,
                                false,
                                true
                            )}
                            onBlur={customReportUseFormik?.handleBlur}
                            onChange={handleStartDateChange}
                        />
                        {/* Tooltip error */}
                        {customReportUseFormik?.errors?.startDate &&
                            customReportUseFormik?.touched?.startDate &&
                            env?.form_validation === true && dateRangePopup &&
                            dateRangePopup && document.getElementById(`startDate_field`) && (
                                <ErrorTooltip
                                    key={dateRangePopup}
                                    target={`startDate_field`}
                                    message={customReportUseFormik?.errors?.startDate}
                                    open={true}
                                />
                            )}
                        <Label className="mb-0 mt-4" htmlFor='startReport'>
                            Select start report
                            {startLoading && <i className="bx bx-loader bx-spin ms-2" />}
                        </Label>
                        <Select
                            options={startReports}
                            id="startReport"
                            name="startReport"
                            className="max-width-10"
                            getOptionLabel={(option: any) =>
                                `${generateReportName(option)} (${dateTimeFormat(option?.local_time, false, false, true)})`}
                            getOptionValue={(option: any) => option.id}
                            onChange={(e: any) => {
                                customReportUseFormik?.setFieldValue("startReport", e?.id);
                            }}
                            onBlur={() => customReportUseFormik.setFieldTouched("startReport", true)}
                            isClearable={true}
                            menuPortalTarget={document.body}
                            styles={customStyle}
                            isLoading={startLoading}
                            isDisabled={startLoading}
                            key={startRefreshkey}
                            noOptionsMessage={() => {
                                return "Sorry no reports found for this date";
                            }}
                        />
                        {customReportUseFormik?.errors && customReportUseFormik?.errors?.startReport &&
                            customReportUseFormik?.touched?.startReport &&
                            env?.form_validation === true && !startLoading && dateRangePopup &&
                            document.getElementById(`startReport`) &&
                            <ErrorTooltip
                                target="startReport"
                                message={customReportUseFormik?.errors?.startReport}
                                open={customReportUseFormik?.errors?.startReport}
                            />
                        }
                    </Col>
                    <Col lg={6} className="px-4">
                        <Label className="mb-0 asteric" htmlFor='endDate_field'>End date</Label>
                        <input
                            type="date"
                            name="endDate"
                            id="endDate_field"
                            min={minDate}
                            max={maxDate}
                            className="datetimepicker text-uppercase form-control mt-0 max-width-10 mb-2"
                            defaultValue={dateTimeFormat(
                                customReportUseFormik?.values.endDate,
                                false,
                                true
                            )}
                            onBlur={customReportUseFormik?.handleBlur}
                            onChange={handleEndDateChange}
                        />

                        {/* Tooltip error */}
                        {customReportUseFormik?.errors?.endDate &&
                            customReportUseFormik?.touched?.endDate &&
                            env?.form_validation === true && dateRangePopup &&
                            dateRangePopup && document.getElementById(`endDate_field`) && (
                                <ErrorTooltip
                                    key={dateRangePopup}
                                    target={`endDate_field`}
                                    message={customReportUseFormik?.errors?.endDate}
                                    open={true}
                                />
                            )}

                        <Label className="mb-0 mt-4" htmlFor='endReport'>
                            Select end report
                            {endLoading && <i className="bx bx-loader bx-spin ms-2" />}
                        </Label>
                        <Select
                            options={endReports}
                            id="endReport"
                            name="endReport"
                            className="max-width-20"
                            getOptionLabel={(option: any) =>
                                `${generateReportName(option)} (${dateTimeFormat(option?.local_time, false, false, true)})`}
                            getOptionValue={(option: any) => option.id}
                            onChange={(e: any) => {
                                customReportUseFormik?.setFieldValue("endReport", e?.id);
                            }}
                            onBlur={() => customReportUseFormik.setFieldTouched("endReport", true)}
                            isClearable={true}
                            menuPortalTarget={document.body}
                            styles={customStyle}
                            isLoading={endLoading}
                            isDisabled={endLoading}
                            key={customReportUseFormik?.values.endDate + endRefreshkey}
                            noOptionsMessage={() => {
                                return "Sorry no reports found for this date";
                            }}
                        />
                        {customReportUseFormik?.errors && customReportUseFormik?.errors?.endReport &&
                            customReportUseFormik?.touched?.endReport &&
                            env?.form_validation === true && !endLoading && dateRangePopup &&
                            document.getElementById(`endReport`) &&
                            <ErrorTooltip
                                target="endReport"
                                message={customReportUseFormik?.errors?.endReport}
                                open={customReportUseFormik?.errors?.endReport}
                            />
                        }
                    </Col>
                    <Row>
                        <Col sm={12} className="d-flex justify-content-end mt-4 mb-3">
                            <Button
                                color="primary"
                                type="button" // keep as button, since we're manually controlling submit
                                disabled={!customReportUseFormik?.isValid || !VesselID || customReportUseFormik.isSubmitting}
                                onClick={() => {
                                    if (customReportUseFormik?.isValid) {
                                        if (isPopupOpen) {
                                            setFilterValues(customReportUseFormik.values);
                                            queryClient.invalidateQueries(queryKeyes.vessel.CIIAssessmentData.key);
                                            setisPopupOpen(false);
                                        } else {
                                            customReportUseFormik.submitForm();
                                        }
                                    }
                                }}
                            >
                                {customReportUseFormik.isSubmitting ? (
                                    <>
                                        <i className="spinner-border spinner-border-sm text-light me-2" />
                                        Generating...
                                    </>
                                ) : (
                                    isPopupOpen ? "Apply" : "Generate"
                                )}
                            </Button>
                        </Col>
                    </Row>
                    <FormValuesDebug
                        values={[
                            customReportUseFormik?.values,
                            customReportUseFormik?.errors,
                            customReportUseFormik?.touched,
                        ]}
                    />
                </Row>
            }
            title="Select date range"
            size="md"
        />
    )
}

export default DateRangePopup